import * as banSlice from "../store/banSlice";
import * as devicesSlice from "../store/devicesSlice";
import { Button, ControlLabel, FormControl, FormGroup, Modal } from "react-bootstrap";
import React, { Component } from "react";
import DeviceKeyInputNormalized from "../components/deviceKeyInputNormalized";
import OverlayLoader from "../components/overlayLoader";
import { connect } from "react-redux";
import { isDeviceKeyValid } from "../common/keyValidation";
import { showModalMessage } from "../store/modalsSlice";
import { unwrapResult } from "@reduxjs/toolkit";

const Mode = {
  Create: 0,
  Edit: 1,
};

const Titles = {
  [Mode.Create]: "Add banned device",
  [Mode.Edit]: "Edit banned device",
};

const SubmitButtonCaptions = {
  [Mode.Create]: "Create",
  [Mode.Edit]: "Save",
};

const BanStatus = {
  NoBan: 0,
  Gz: 1,
  Xml: 2,
};

const BanStatusCaptions = {
  [BanStatus.NoBan]: "No ban (0)",
  [BanStatus.Gz]: "GZ (1)",
  [BanStatus.Xml]: "XML (2)",
};

class BanDialog extends Component {
  initState = {
    banStatus: BanStatus.NoBan,
    deviceKey: "",
    loadedDevice: null,
  };

  constructor(props) {
    super(props);
    this.handleClose = this.props.onClose;

    this.state = this.initState;

    this.onShow = this.onShow.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    this.onDeviceKeyChange = this.onDeviceKeyChange.bind(this);
    this.onBanStatusChange = this.onBanStatusChange.bind(this);
  }

  static get Mode() {
    return Mode;
  }

  static get BanStatusCaptions() {
    return BanStatusCaptions;
  }

  onShow() {
    this.setState(this.initState);

    if (this.props.mode === Mode.Edit) {
      if (this.props.banStatus !== undefined) {
        this.setState({ banStatus: this.props.banStatus });
      }
    }
  }

  onClose() {
    this.handleClose();
    this.setState(this.initState);
  }

  isLoading() {
    if (this.props.mode === Mode.Create) {
      return this.props.isBanDeviceCreating || this.props.isDeviceLoading;
    }

    if (this.props.mode === Mode.Edit) {
      return this.props.isBanDeviceUpdating;
    }
  }

  getLoaderMessage() {
    if (this.props.isDeviceLoading) {
      return "Device loading...";
    }

    if (this.props.mode === Mode.Create) {
      return "Banned device creating...";
    }

    if (this.props.mode === Mode.Edit) {
      return "Banned device editting...";
    }

    return "";
  }

  isSubmitButtonDisabled() {
    if (this.props.mode === Mode.Edit) {
      return this.isLoading() || (+this.state.banStatus === +this.props.banStatus);
    }

    return this.isLoading() ||
      !this.isCorrectDeviceKey() ||
      this.isDeviceAlreadyWasBaned(this.state.deviceKey, this.props.bannedDevices) ||
      this.state.loadedDevice === null;
  }

  onSubmit() {
    const { loadedDevice, banStatus } = this.state;
    const { mode, id } = this.props;

    let submitPromise = null;

    switch (mode) {
    case Mode.Create:
      submitPromise = this.props.addBannedDevice({ banStatus, deviceId: loadedDevice.id });
      break;
    case Mode.Edit:
      submitPromise = this.props.updateBannedDevice({ banStatus, id });
      break;
    default:
      submitPromise = null;
    }

    this.onClose();

    if (submitPromise === null) {
      this.props.showModalMessage("Error", "Unknown dialog mode");
      return;
    }

    return submitPromise.
      then(unwrapResult).
      catch((e) => {
        this.props.showModalMessage("Error", this.props.onSubmitError.toString());
      });
  }

  onDeviceKeyChange(normalizedDeviceKey) {
    this.setState({ deviceKey: normalizedDeviceKey }, this.loadDeviceInfo);
  }

  onBanStatusChange(e) {
    this.setState({ banStatus: e.target.value });
  }

  isCorrectDeviceKey() {
    return isDeviceKeyValid(this.state.deviceKey.trim());
  }

  isDeviceAlreadyWasBaned(deviceKey, rejectedDevices) {
    return deviceKey && rejectedDevices && rejectedDevices.find(
      (rejectedDevice) => deviceKey === rejectedDevice.device.key
    );
  }

  loadDeviceInfo() {
    if (this.isCorrectDeviceKey()) {
      this.props.getDeviceByKey({ deviceKey: this.state.deviceKey.trim() }).
        then(unwrapResult).
        then((device) => {
          this.setState({ loadedDevice: device });
        });
    } else {
      this.setState({ loadedDevice: null });
    }
  }

  renderDynamic() {
    return this.isCorrectDeviceKey() ? this.renderLoadingResults() : this.renderDeviceKeyError();
  }

  renderLoadingResults() {
    const { loadedDevice } = this.state;
    const { isDeviceLoading, bannedDevices } = this.props;

    if (loadedDevice === null) {
      if (isDeviceLoading) {
        return null;
      }

      return (
        <FormControl.Static className="text-danger">
          Device is not found
        </FormControl.Static>
      );
    }

    if (loadedDevice && this.isDeviceAlreadyWasBaned(loadedDevice.key, bannedDevices)) {
      return (
        <FormControl.Static className="text-danger">
          This device was already baned
        </FormControl.Static>
      );
    }

    return (
      <FormControl.Static className="text-success">
        Device has been found
      </FormControl.Static>
    );
  }

  renderDeviceKeyError() {
    const message =
      this.state.deviceKey.length === 0
        ? {
          className: "text-info",
          text: "Enter device key",
        }
        : {
          className: "text-danger",
          text: "Device key is not correct",
        };

    return (
      <FormControl.Static className={message.className}>
        {message.text}
      </FormControl.Static>
    );
  }

  render() {
    const { banStatus } = this.state;
    const { show, mode } = this.props;

    return (
      <Modal
        className="ban-device-add-dialog"
        bsSize="small"
        show={show}
        onShow={this.onShow}
        onHide={this.onClose}
        backdrop="static"
      >
        <Modal.Header>
          <Modal.Title>{Titles[mode]}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <OverlayLoader
            active={this.isLoading()}
            text={this.getLoaderMessage()}
          >
            {mode !== Mode.Edit ? (
              <FormGroup controlId="fcDeviceKey">
                <ControlLabel>Device key</ControlLabel>
                <DeviceKeyInputNormalized onChange={this.onDeviceKeyChange} />
                {this.renderDynamic()}
              </FormGroup>
            ) : (
              ""
            )}

            <FormGroup controlId="fcBanStatus">
              <ControlLabel>Ban status</ControlLabel>
              <FormControl
                componentClass="select"
                placeholder="Ban status"
                value={banStatus}
                onChange={this.onBanStatusChange}
              >
                {Object.entries(BanStatusCaptions).map(([value, text]) => (
                  <option key={value} value={value}>
                    {text}
                  </option>
                ))}
              </FormControl>
            </FormGroup>
          </OverlayLoader>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={this.onSubmit}
            disabled={this.isSubmitButtonDisabled()}
          >
            {SubmitButtonCaptions[mode]}
          </Button>
          <Button onClick={this.onClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

const mapStateToProps = (state) => ({
  bannedDevices: banSlice.banSelectors.selectAll(state),
  isBanDeviceCreating: banSlice.banSelectors.isCreating(state),
  isBanDeviceUpdating: banSlice.banSelectors.isUpdating(state),
  isDeviceLoading: devicesSlice.devicesSelectors.isFetching(state),
  onSubmitError: banSlice.banSelectors.creatingUpdatingError(state),
});

const actionCreators = {
  addBannedDevice: banSlice.addBannedDevice,
  getDeviceByKey: devicesSlice.fetchByKey,
  showModalMessage: (title, text) => showModalMessage({ text, title }),
  updateBannedDevice: banSlice.updateBannedDevice,
};

export default connect(mapStateToProps, actionCreators)(BanDialog);



// WEBPACK FOOTER //
// ./src/containers/banDialog.js